import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Typography,
  Box,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import api from '../../api/axios';

const STATUS_OPTIONS = [
  { value: 'NEW', label: 'Новый' },
  { value: 'SUCCESS', label: 'Выполнен' },
  { value: 'CLOSE', label: 'Закрыт' },
  { value: 'PAYMENT_PENDING', label: 'Ожидает оплату' },
  { value: 'PAID', label: 'Оплачен' },
  { value: 'FAILED', label: 'Ошибка оплаты' },
];

export default function OrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState<any | null>(null);

  useEffect(() => {
    api
      .get(`/api/admin/orders/${id}`)
      .then((res) => {
        setOrder(res.data);
      })
      .catch(console.error);
  }, [id]);

  const handleFieldChange = (field: string, value: any) => {
    setOrder((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index: number, field: string, value: number) => {
    setOrder((prev: any) => {
      const items = [...prev.itemsDetailed];
      items[index] = {
        ...items[index],
        [field]: value,
      };
      return { ...prev, itemsDetailed: items };
    });
  };

  const getItemCount = (item: any) => {
    if (item.type === 'sensor') return 4;
    if (item.type === 'bolts') return 1;
    if (item.type === 'delivery') return 1;
    return Number(item.count || 1);
  };

  const getItemLineTotal = (item: any) => {
    const price = Number(item.price || 0);
    const count = getItemCount(item);
    return price * count;
  };

  const getItemTypeLabel = (item: any) => {
    if (item.type === 'sensor') return 'Датчик';
    if (item.type === 'bolts') return 'Крепёж';
    if (item.type === 'delivery') return 'Доставка';
    if (item.type === 'tyre') return 'Резина';
    if (item.type === 'wheel_set') return 'Комплект дисков';
    if (item.type === 'wheel') return 'Диск';
    return 'Товар';
  };

  const calculateTotal = () => {
    return order.itemsDetailed.reduce((sum: number, item: any) => {
      return sum + getItemLineTotal(item);
    }, 0);
  };

  if (!order) return <Typography>Загрузка...</Typography>;

  return (
    <>
      <Button variant="outlined" component={Link} to={`/orders`} sx={{ mb: 2 }}>
        Назад
      </Button>

      <Box p={3}>
        <Typography variant="h5" gutterBottom>
          Заказ от {order.name}
        </Typography>

        <Typography>id заказа: {order._id}</Typography>

        <Typography>
          Способ доставки: {order.deliveryMethod === 'courier' ? 'Доставка' : 'Самовывоз'}
        </Typography>

        <Typography>Дата и время: {new Date(order.createdAt).toLocaleString()}</Typography>

        <Stack spacing={2} mt={2}>
          <TextField
            label="Телефон"
            fullWidth
            value={order.phone || ''}
            onChange={(e) => handleFieldChange('phone', e.target.value)}
          />

          <TextField
            label="Email"
            fullWidth
            value={order.email || ''}
            onChange={(e) => handleFieldChange('email', e.target.value)}
          />

          <TextField
            label="Адрес"
            fullWidth
            value={order.address || ''}
            onChange={(e) => handleFieldChange('address', e.target.value)}
          />

          <TextField
            label="Статус"
            select
            fullWidth
            value={order.status || 'NEW'}
            onChange={(e) => handleFieldChange('status', e.target.value)}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6">Товары:</Typography>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Наименование</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Источник</TableCell>
              <TableCell>Цена</TableCell>
              <TableCell>Количество</TableCell>
              <TableCell>Сумма</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {order.itemsDetailed.map((item: any, i: number) => {
              const readonlyCount =
                item.type === 'sensor' || item.type === 'bolts' || item.type === 'delivery';

              return (
                <TableRow key={i}>
                  <TableCell>{item.product?.name || item.name || 'Не найден'}</TableCell>

                  <TableCell>{getItemTypeLabel(item)}</TableCell>

                  <TableCell>{item.isMy ? '-' : item.source || '-'}</TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={item.price ?? 0}
                      onChange={(e) => handleItemChange(i, 'price', +e.target.value)}
                      inputProps={{ min: 0 }}
                    />
                  </TableCell>

                  <TableCell>
                    {readonlyCount ? (
                      String(getItemCount(item))
                    ) : (
                      <TextField
                        type="number"
                        size="small"
                        value={item.count ?? 1}
                        onChange={(e) => handleItemChange(i, 'count', +e.target.value)}
                        inputProps={{
                          min: item.type === 'tyre' ? 2 : 1,
                          step: item.type === 'tyre' ? 2 : 1,
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell>{getItemLineTotal(item)} ₽</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Typography variant="h6" align="right" sx={{ mt: 2 }}>
          Общая сумма: {calculateTotal()} ₽
        </Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 3 }}
        onClick={() => {
          api
            .post(`/api/admin/orders`, {
              method: 'UPDATE',
              body: {
                id: order._id,
                phone: order.phone,
                email: order.email,
                address: order.address,
                status: order.status,
                items: order.itemsDetailed.map((item: any) => ({
                  productId: item.productId || item._id,
                  type: item.type,
                  price: Number(item.price || 0),
                  count:
                    item.type === 'sensor'
                      ? 4
                      : item.type === 'bolts'
                        ? 1
                        : item.type === 'delivery'
                          ? 1
                          : Number(item.count || 1),
                  name: item.name,
                })),
                needBolts: order.needBolts,
              },
            })
            .then(() => alert('Сохранено успешно'))
            .catch((err) => {
              console.error(err);
              alert('Ошибка при сохранении');
            });
        }}
      >
        Сохранить
      </Button>
    </>
  );
}